/**
 * Academic Excellence Hero
 * This script handles:
 *   - Dynamically creating a <sup> element for any %, $, or # symbols in the statistic element
 *   - Adjusting font-size on window resize to ensure the statistic fills the text container as much as possible without
 *     overflowing or exceeding the max font size for the breakpoint
 */

class BuxAcademicExcellenceHero {
  static config = {
    fontSizeMax: {
      belowLg: 6.9375,
      aboveLg: 10.375,
    },
    fontSizeStep: 0.0625,
    superScriptRatio: 0.667,
  };

  rootElement: HTMLElement;

  constructor(rootElement: HTMLElement) {
    this.rootElement = rootElement;

    this.initStatisticStyling();

    window.addEventListener("resize", this.maybeAdjustFontSize.bind(this), { passive: true });
    this.setInitialFontSize();
    requestAnimationFrame(() => this.maybeAdjustFontSize());
  }

  /** Returns true if the viewport is at the large breakpoint (960px+). */
  get isAtLg() {
    return window.innerWidth >= 960;
  }

  /** Queries and returns the statistic element within the root. */
  get statElement() {
    return this.rootElement?.querySelector<HTMLElement>(".header__statistic");
  }

  /** Queries and returns the `<sup>` element inside the statistic, if present. */
  get supElement() {
    return this.statElement?.querySelector<HTMLElement>("sup");
  }

  /** Returns true if the statistic element is wider than its root container. */
  get statIsOverflowing() {
    if (!this.rootElement || !this.statElement) return false;
    return this.statElement.clientWidth > this.rootElement.clientWidth;
  }

  /** Returns the breakpoint-appropriate maximum font size in rem. */
  get maxFontSize() {
    return BuxAcademicExcellenceHero.config.fontSizeMax[
      this.isAtLg ? "aboveLg" : "belowLg"
    ];
  }

  /** Parses and returns the statistic element's current font size in rem. */
  get currentFontSize() {
    if (!this.statElement) return 0;
    return Number.parseFloat(
      this.statElement.style.fontSize.replace("rem", ""),
    );
  }

  /** Returns the maximum allowed font size for the `<sup>` element, scaled by the superscript ratio. */
  get maxSupFontSize() {
    return this.maxFontSize * BuxAcademicExcellenceHero.config.superScriptRatio;
  }

  /**
   * Sets the statistic and superscript font sizes to their breakpoint maximums.
   * Called once on initialization before dynamic adjustment begins.
   */
  setInitialFontSize() {
    if (!this.statElement) return;

    this.statElement.style.fontSize = `${this.maxFontSize}rem`;
    if (this.supElement) {
      this.supElement.style.fontSize = `${this.maxSupFontSize}rem`;
    }
  }

  /**
   * Checks whether the statistic is overflowing or above the max font size,
   * then delegates to the appropriate increase or decrease method.
   * Called on every resize event and after the initial animation frame.
   */
  maybeAdjustFontSize() {
    if (!this.statElement) return;
    const isOverflowing =
      this.statElement.clientWidth > this.rootElement.clientWidth;

    if (isOverflowing || this.currentFontSize > this.maxFontSize) {
      this.decreaseFontSizeUntilFits();
    } else {
      this.increaseFontSizeUntilFits();
    }
  }

  /**
   * Recursively decreases the font size by one step until the statistic
   * fits within the container and does not exceed the max font size.
   */
  decreaseFontSizeUntilFits() {
    if (!this.statElement) return;
    this.decreaseFontSize();
    if (this.statIsOverflowing || this.currentFontSize > this.maxFontSize) {
      this.decreaseFontSizeUntilFits();
    }
  }

  /**
   * Reduces the statistic font size by one step (BuxAcademicExcellenceHero.config.fontSizeSteprem) and scales
   * the superscript proportionally using the configured ratio.
   */
  decreaseFontSize() {
    if (!this.statElement) return;

    const numberFontSize = Number.parseFloat(
      this.statElement.style.fontSize.replace("rem", ""),
    );
    const newNumberFontSize = numberFontSize - BuxAcademicExcellenceHero.config.fontSizeStep;
    this.statElement.style.fontSize = `${newNumberFontSize}rem`;
    if (this.supElement) {
      const newSuperScriptFontSize =
        newNumberFontSize * BuxAcademicExcellenceHero.config.superScriptRatio;
      this.supElement.style.fontSize = `${newSuperScriptFontSize}rem`;
    }
  }

  /**
   * Recursively increases the font size by one step until the statistic
   * reaches the breakpoint maximum or begins to overflow its container.
   * Delegates back to `decreaseFontSizeUntilFits` if an overflow is detected.
   */
  increaseFontSizeUntilFits() {
    if (!this.statElement || !this.rootElement) return;
    const numberMax =
      BuxAcademicExcellenceHero.config.fontSizeMax[
        this.isAtLg ? "aboveLg" : "belowLg"
      ];
    const numberFontSize = this.currentFontSize;
    const isAtMax = numberFontSize >= numberMax;
    if (isAtMax) return;
    if (this.statIsOverflowing) {
      this.decreaseFontSizeUntilFits();
      return;
    }
    this.increaseFontSize(numberFontSize);
    if (!this.statIsOverflowing) {
      this.increaseFontSizeUntilFits();
      return;
    }
  }

  /**
   * Increases the statistic font size by one step (BuxAcademicExcellenceHero.config.fontSizeSteprem) and scales
   * the superscript proportionally using the configured ratio.
   * @param numberFontSize - The current font size in rem before the increase.
   */
  increaseFontSize(numberFontSize: number) {
    if (!this.statElement) return;
    const newNumberFontSize = numberFontSize + BuxAcademicExcellenceHero.config.fontSizeStep;
    this.statElement.style.fontSize = `${newNumberFontSize}rem`;
    if (this.supElement) {
      const newSuperScriptFontSize =
        newNumberFontSize * BuxAcademicExcellenceHero.config.superScriptRatio;
      this.supElement.style.fontSize = `${newSuperScriptFontSize}rem`;
    }
  }

  /**
   * Parses the statistic element's text content and wraps any `%`, `$`, `#`
   * symbol or ordinal suffix (st/nd/rd/th) in a `<sup>` element if one does
   * not already exist. Wraps the remaining numeric text in a `<span>` so both
   * parts can be sized independently via `inline-block` display.
   */
  initStatisticStyling() {
    if (!this.statElement) return;
    let sup = this.statElement.querySelector("sup");
    if (!sup) {
      const text = this.statElement.textContent;
      if (!text) return;

      const symbolIndex = text.search(/[#$%]/);
      const ordinalsIndex = text.search(/\d((?:st)|(?:nd)|(?:rd)|(?:th))/);
      const hasSymbol = symbolIndex !== -1;
      const hasOrdinals = ordinalsIndex !== -1;
      if (!hasSymbol && !hasOrdinals) {
        return;
      }
      if (hasSymbol && hasOrdinals) {
        return;
      }
      sup = document.createElement("sup");
      if (hasSymbol) {
        const symbolChar = text.slice(symbolIndex, symbolIndex + 1);
        const number = text.replace(symbolChar, "");
        sup.append(document.createTextNode(symbolChar));
        if (symbolIndex === 0) {
          this.statElement.replaceChildren(
            sup,
            document.createTextNode(number),
          );
        } else {
          this.statElement.replaceChildren(
            document.createTextNode(number),
            sup,
          );
        }
      } else if (hasOrdinals) {
        const number = text.slice(0, ordinalsIndex + 1);
        const ordinalText = text.slice(ordinalsIndex + 1);
        sup.append(document.createTextNode(ordinalText));
        this.statElement.replaceChildren(
          document.createTextNode(number),
          sup,
        );
      }
    }

    sup.style.display = "inline-block";
    const text = [...this.statElement.childNodes].find((n) => {
      return n.nodeType === 3 && n.textContent?.length;
    });
    if (!text || !text.textContent) {
      return;
    }
    const span = document.createElement("span");
    span.textContent = text.textContent;
    span.style.display = "inline-block";
    text.textContent = "";
    this.statElement.replaceChild(span, text);
  }
}

const initAcademicExcellenceHero = () => {
  const heroInstances = document.querySelectorAll<HTMLElement>(
    ".bux-academic-excellence__hero",
  );
  for (const instance of heroInstances) {
    const bodyElement = instance.querySelector<HTMLElement>(".hero__body");
    if (!bodyElement || !(bodyElement instanceof HTMLElement)) continue;
    new BuxAcademicExcellenceHero(bodyElement);
  }
}

window.addEventListener("load", initAcademicExcellenceHero);

export default BuxAcademicExcellenceHero;
export { initAcademicExcellenceHero }
